import humps from 'humps';

import type { OptimizedImageUrl } from '@ezugi/types';
import isObject from '@ezugi/utils/predicates/isObject';

export type OptimizedImageSrc = {
  src: string;
  srcSet?: string; // 'url 1x, url 2x, url 3x'
};

export function imageUrlToSrc(
  imageUrl: string | OptimizedImageUrl | null | undefined
): OptimizedImageSrc {
  if (typeof imageUrl === 'string') {
    return { src: imageUrl };
  }
  if (!isOptimizedImageUrl(imageUrl)) {
    return { src: '' };
  }
  const { original, small, medium, large } = imageUrl;

  return {
    src: original,
    srcSet: [
      `${small || original} 1x`,
      `${medium || original} 2x`,
      `${large || original} 3x`,
    ].join(', '),
  };
}

export function parseImageUrl(
  imageUrl: string | OptimizedImageUrl | null | undefined
): OptimizedImageSrc {
  /**
   * Uncomment the line below to test all images with mocked responsive images
   * TODO responsive-image: Remove the line below after the BE is ready
   */
  // const imageUrl = mockOptimizedImageUrl();

  const urlData = isObject(imageUrl)
    ? humps.camelizeKeys(imageUrl) as OptimizedImageUrl
    : imageUrl;
  return imageUrlToSrc(urlData);
}

export function isOptimizedImageUrl(url: any): url is OptimizedImageUrl {
  if (!url || typeof url !== 'object') {
    return false;
  }
  const urlObj = humps.camelizeKeys(url);
  return (
    'original' in urlObj
    && 'small' in urlObj
    && 'medium' in urlObj
    && 'large' in urlObj
  );
}

export function isOptimizedImageSrc(url: any): url is OptimizedImageSrc {
  if (!url || typeof url !== 'object') {
    return false;
  }
  return ('src' in url && 'srcSet' in url);
}

export function addRootToPath(
  root: string | null | undefined,
  path: string | OptimizedImageUrl | null | undefined
): string | OptimizedImageUrl | null {
  if (!root || !path) {
    return null;
  }
  if (isOptimizedImageUrl(path)) {
    // It concatenates the root to each object's images:
    // { small: path, ... } => { small: root + path, ... }
    return Object.fromEntries(
      Object.entries(path).map(
        ([ key, value ]) => [ key, root + value ]
      )
    ) as OptimizedImageUrl;
  }
  if (typeof path === 'string') {
    return root + path;
  }
  return root;
}

/**
 * This is used to add mocked optimized images to the parseImageUrl function
 * TODO responsive-image: Remove this after the BE is ready
 * @param urlHasRoot
 * @returns
 */
export function mockOptimizedImageUrl(urlHasRoot?: boolean): OptimizedImageUrl {
  const url = urlHasRoot ? '/500x500/fc0/F00&text=' : 'https://dummyimage.com/500x500/fc0/F00&text=';
  return {
    original: `${url}0x`,
    small: `${url}1x`,
    medium: `${url}2x`,
    large: `${url}3x`,
  };
}
